import { useEffect, useState } from 'react';

import LinearProgress from '@zenbusiness/zui/LinearProgress';
import { useNavigate } from 'react-router-dom';

import BankingTitle from '#client/components/BankingTitle';
import { HubContainerGridStyled } from '#client/components/Grid';
import { ZB_BANKING_SUNSET_PH3 } from '#client/constants/experimentsFlags';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useBankingPath from '#client/hooks/useBankingPath';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useCheckTransactionContext from '#client/hooks/useCheckTransactionContext';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useFeatureFlag from '#client/hooks/useFeatureFlag';
import useSessionStorage from '#client/hooks/useSessionStorage';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { BANKING_TRANSITION_SEGMENT } from '#client/pages/BankingTransition/constants';

import SharedNavigateModals from '../SharedNavigateModals';
import NotFundedOptions from './components/NotFundedOptions';
import RudderStackEvent from './components/RudderStackEvent';

export default function DashboardNotFunded() {
  const [loadingNavigate, setLoadingNavigate] = useState(true);
  const { isOpeningModal } = useCheckOpenModal();
  const bankingPath = useBankingPath();

  const { isInvalidStatus, appStatusError } = useApplicationStatusContext();
  const {
    isEmptyUnitAccountId,
    error: unitAccountIdError,
    isEnabled,
    isEnrolled,
    isFetchingMfaStatus,
    isValidMfa,
    loading: loadingUnitAccountId,
    mfaError
  } = useUnitAccountId();
  const {
    error: transactionError,
    hasTransactionOrPayments,
    isEmptyTransaction,
    loading: loadingTransaction
  } = useCheckTransactionContext();
  const dashboardUrl = `${bankingPath}/dashboard`;
  const transitionFlowUrl = `${bankingPath}/${BANKING_TRANSITION_SEGMENT}`;
  const navigate = useNavigate();

  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const [, setStopRedirectDashboardNotFunded] = useSessionStorage(
    `stop_redirect_dashboard_notfunded.${businessEntityUuid}`,
    false
  );

  const [isZbBankingSunsetPh3Enabled, isLoadingFlag] = useFeatureFlag(ZB_BANKING_SUNSET_PH3);

  const invalidMFA = isEnabled === false || isEnrolled === false || isValidMfa === false;
  const loading =
    loadingUnitAccountId || loadingTransaction || isFetchingMfaStatus || isLoadingFlag;
  const error = appStatusError || unitAccountIdError || mfaError || transactionError;

  useEffect(() => {
    if (loading) {
      return;
    }

    if (
      isEmptyUnitAccountId ||
      isInvalidStatus ||
      invalidMFA ||
      hasTransactionOrPayments ||
      error
    ) {
      navigate(dashboardUrl);
      return;
    }

    // BE-6762: no entry point may show the Account Funding screen while the sunset flag is
    // on, including a direct/bookmarked visit that never went through Dashboard's own redirect.
    if (isZbBankingSunsetPh3Enabled) {
      // Set the same guard Dashboard's own redirect sets, so exiting the transition flow back
      // to the dashboard doesn't bounce the customer straight back in here.
      setStopRedirectDashboardNotFunded(true);
      navigate(transitionFlowUrl, { replace: true });
    }
  }, [
    dashboardUrl,
    transitionFlowUrl,
    isZbBankingSunsetPh3Enabled,
    error,
    hasTransactionOrPayments,
    invalidMFA,
    isEmptyUnitAccountId,
    isInvalidStatus,
    loading,
    navigate,
    setStopRedirectDashboardNotFunded
  ]);

  useEffect(() => {
    if (isEmptyTransaction && !isZbBankingSunsetPh3Enabled) {
      setLoadingNavigate(false);
    }
  }, [isEmptyTransaction, isZbBankingSunsetPh3Enabled]);

  if (loadingNavigate) {
    return <LinearProgress />;
  }

  return isOpeningModal ? (
    <SharedNavigateModals />
  ) : (
    <HubContainerGridStyled marginTop={{ xs: '32px!important', md: '48px!important' }}>
      <BankingTitle isDashboardNotFunded />
      <NotFundedOptions />
      <RudderStackEvent />
    </HubContainerGridStyled>
  );
}
